import { SessionStorageConstants } from "@/types/SessionStorageConstants";

// Every route in the app is code-split, so navigating to a section the user has not opened in this
// tab downloads that section's chunk on the spot. When that one request fails (a flaky connection,
// a proxy that drops it, a CDN edge that hiccups) webpack throws a ChunkLoadError and the whole app
// unmounts into the "Oops! Something went wrong!" page, from which only a manual refresh recovers —
// nothing is actually broken, the file is still there (LS-1336: 19 of these in one morning, all
// against assets that had been deployed for five days). A full reload is exactly what the user
// would do, so do it for them, once.

/** How long one automatic reload "counts" for a page. A second chunk failure on the same path
 *  inside this window means reloading did not help (the network really is down, or the asset
 *  really is gone), so the boundary must fall through to the error page instead of looping. */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

const CHUNK_LOAD_MESSAGE = /Loading (?:CSS )?chunk [\w.-]+ failed/i;

/** True for the errors webpack raises when an async chunk cannot be fetched or evaluated. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === "ChunkLoadError") {
    return true;
  }
  return typeof message === "string" && CHUNK_LOAD_MESSAGE.test(message);
}

type ReloadStorage = Pick<Storage, "getItem" | "setItem">;

/**
 * Decide whether this chunk failure may be answered with an automatic reload, and record the
 * attempt when it may. Exactly one reload per path per window; if the storage is unavailable
 * (private mode, disabled) the answer is "no" — a reload we cannot remember is a reload loop.
 */
export function claimChunkReload(
  pathname: string,
  storage: ReloadStorage | null = safeSessionStorage(),
  now: number = Date.now()
): boolean {
  if (!storage) {
    return false;
  }
  const key = `${SessionStorageConstants.CHUNK_RELOAD_AT}:${pathname}`;
  try {
    const previous = Number(storage.getItem(key));
    if (previous > 0 && now - previous < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    storage.setItem(key, String(now));
    return true;
  } catch {
    return false;
  }
}

function safeSessionStorage(): ReloadStorage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
